import type { Id } from "@Aspecto/backend/convex/_generated/dataModel";

import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Check,
  CircleStar,
  EllipsisVertical,
  EyeOff,
  Info,
  Lock,
  LogOut,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { CommunityType } from "@/lib/community-templates";
import { COMMUNITY_TYPE_ICONS, COMMUNITY_TYPE_SHORT_LABELS } from "@/lib/community-templates";

import { GroupBreadcrumbs } from "@/components/groups/group-breadcrumbs";
import { JoinGateProvider } from "@/components/groups/join-gate-dialog";
import { AppPageContent } from "@/components/layout/app-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TabNav } from "@/components/ui/tab-nav";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GROUP_VISIBILITY_META, type GroupVisibility } from "@/lib/group-visibility";
import { useGroupLabel } from "@/hooks/use-group-label";
import { useCurrentUser } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/groups/$groupId")({
  component: GroupDetailLayout,
});

function GroupDetailLayout() {
  const { groupId } = Route.useParams();
  const navigate = useNavigate();
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const groupLabel = useGroupLabel();

  const { data: group } = useSuspenseQuery(
    convexQuery(api.groups.getGroupById, { groupId: groupId as Id<"groups"> }),
  );

  const { data: currentUser } = useCurrentUser();

  const { data: membership } = useQuery(
    convexQuery(api.groupMembers.getGroupMembership, {
      groupId: groupId as Id<"groups">,
    }),
  );

  const { data: groupTags } = useQuery(
    convexQuery(api.tags.getGroupTags, { groupId: groupId as Id<"groups"> }),
  );

  const { mutate: leaveGroup, isPending: isLeaving } = useMutation({
    mutationFn: useConvexMutation(api.groupMembers.leaveGroup),
    onSuccess: () => {
      setLeaveDialogOpen(false);
      navigate({ to: "/app/groups" });
    },
  });

  const { mutate: acceptInvitation, isPending: isAccepting } = useMutation({
    mutationFn: useConvexMutation(api.groupInvitations.acceptGroupInvitation),
  });

  const { mutate: joinGroup, isPending: isJoining } = useMutation({
    mutationFn: useConvexMutation(api.groupMembers.joinGroup),
    onSuccess: () => {
      toast.success(
        "Congratulations, now you are a member and can participate and receive notifications.",
      );
    },
  });

  const { mutate: requestToJoin, isPending: isRequesting } = useMutation({
    mutationFn: useConvexMutation(api.groupJoinRequests.requestToJoinGroup),
  });

  if (!group) {
    return (
      <AppPageContent>
        <GroupBreadcrumbs groupsLabel={groupLabel.plural} />
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed p-12 text-center">
          <CircleStar className="mb-5 h-12 w-12 text-muted-foreground/60" />
          <h3 className="mb-2 text-lg font-semibold">{groupLabel.singular} not found</h3>
          <p className="mb-5 text-sm text-muted-foreground">
            This {groupLabel.singularLower} does not exist or you don&apos;t have access to it.
          </p>
        </div>
      </AppPageContent>
    );
  }

  const isAdmin =
    membership?.isCompanyAdmin ||
    membership?.role === "group_admin" ||
    (group.isGlobal && currentUser?.role === "company_admin");

  const canLeave =
    !group.isGlobal &&
    !group.isLeadsSpace &&
    membership?.isMember &&
    group.createdById !== currentUser?._id;

  const tabs = [
    { to: "/app/groups/$groupId" as const, label: "Overview", params: { groupId } },
    { to: "/app/groups/$groupId/posts" as const, label: "Updates", params: { groupId } },
    { to: "/app/groups/$groupId/events" as const, label: "Events", params: { groupId } },
    { to: "/app/groups/$groupId/members" as const, label: "Members", params: { groupId } },
    ...(group.enableResources
      ? [{ to: "/app/groups/$groupId/resources" as const, label: "Resources", params: { groupId } }]
      : []),
    ...(isAdmin
      ? [
          {
            label: "Admin",
            menuItems: [
              {
                to: "/app/groups/$groupId/admin" as const,
                label: "Settings",
                params: { groupId },
              },
              {
                to: "/app/groups/$groupId/admin/members" as const,
                label: "Members admin",
                params: { groupId },
              },
              {
                to: "/app/groups/$groupId/analytics" as const,
                label: "Analytics",
                params: { groupId },
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <AppPageContent variant="medium">
      <div className="mb-6">
        <GroupBreadcrumbs
          groupId={groupId}
          groupName={group.name}
          groupsLabel={groupLabel.plural}
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold">{group.name}</h1>
            {membership?.isMember && isAdmin && !group.isGlobal && (
              <Badge variant="secondary" className="mt-2 px-3 py-1.5 text-sm font-medium sm:hidden">
                Lead
              </Badge>
            )}
            {group.purpose && (
              <p className="mt-1 wrap-break-word text-muted-foreground">{group.purpose}</p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {group.communityType &&
                group.communityType !== "blank" &&
                (() => {
                  const TypeIcon = COMMUNITY_TYPE_ICONS[group.communityType as CommunityType];
                  return (
                    <Badge variant="outline" className="gap-1.5">
                      <TypeIcon className="h-3.5 w-3.5" />
                      {COMMUNITY_TYPE_SHORT_LABELS[group.communityType as CommunityType]}
                    </Badge>
                  );
                })()}
              {!group.isGlobal &&
                group.visibility &&
                (() => {
                  const meta = GROUP_VISIBILITY_META[group.visibility as GroupVisibility];
                  if (!meta) return null;
                  const VisibilityIcon = meta.Icon;
                  return (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Badge variant="outline" className="cursor-default gap-1.5">
                              <VisibilityIcon className="h-3.5 w-3.5" />
                              {meta.label}
                            </Badge>
                          }
                        />
                        <TooltipContent>{meta.tooltip}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })()}
              <Badge variant="outline">
                {(group.memberCount ?? 0) < 10 ? (
                  <>
                    <Users className="mr-1 h-3 w-3" />
                    New
                  </>
                ) : (
                  <>
                    <Users className="mr-1 h-3 w-3" />
                    {group.memberCount} members
                  </>
                )}
              </Badge>
              {groupTags &&
                groupTags.length > 0 &&
                groupTags.filter(Boolean).map((tag) => (
                  <Badge key={tag!._id} variant="secondary">
                    {tag!.name}
                  </Badge>
                ))}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:pt-1">
            {!membership?.isMember && group.hasPendingInvitation && group.invitationToken && (
              <Button
                size="sm"
                onClick={() => acceptInvitation({ token: group.invitationToken! })}
                disabled={isAccepting}
              >
                {isAccepting ? "Joining..." : `+ Join ${groupLabel.singular}`}
              </Button>
            )}
            {!membership?.isMember &&
              !group.isGlobal &&
              group.visibility === "public" &&
              !group.hasPendingInvitation && (
                <Button
                  size="sm"
                  onClick={() => joinGroup({ groupId: groupId as Id<"groups"> })}
                  disabled={isJoining}
                >
                  {isJoining ? "Joining..." : `+ Join ${groupLabel.singular}`}
                </Button>
              )}
            {!membership?.isMember &&
              !group.isGlobal &&
              group.visibility === "private" &&
              !group.hasPendingInvitation &&
              !group.hasPendingJoinRequest && (
                <Button
                  size="sm"
                  onClick={() => requestToJoin({ groupId: groupId as Id<"groups"> })}
                  disabled={isRequesting}
                >
                  {isRequesting ? "Requesting..." : "Request to Join"}
                </Button>
              )}
            {!membership?.isMember &&
              group.visibility === "private" &&
              group.hasPendingJoinRequest && (
                <Badge
                  variant="outline"
                  className="border-blue-200 bg-blue-50 px-3 py-1 text-blue-700"
                >
                  Request Pending
                </Badge>
              )}
            {membership?.isMember && isAdmin && !group.isGlobal && (
              <Badge
                variant="secondary"
                className="hidden px-3 py-1.5 text-sm font-medium sm:inline-flex"
              >
                Lead
              </Badge>
            )}
            {membership?.isMember && !isAdmin && !group.isGlobal && (
              <>
                <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-sm font-medium">
                  <Check className="h-3.5 w-3.5" />
                  Joined
                </Badge>
                {canLeave && (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                      <EllipsisVertical className="h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => setLeaveDialogOpen(true)}
                      >
                        <LogOut className="h-4 w-4" />
                        Leave {groupLabel.singular}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {(group.visibility === "private" || group.visibility === "secret") &&
      !membership?.isMember ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed p-12 text-center">
          {group.visibility === "secret" ? (
            <EyeOff className="mb-5 h-12 w-12 text-muted-foreground/60" />
          ) : (
            <Lock className="mb-5 h-12 w-12 text-muted-foreground/60" />
          )}
          <h3 className="mb-2 text-lg font-semibold">
            This is a {group.visibility} {groupLabel.singularLower}
          </h3>
          <p className="mb-5 max-w-md text-sm text-muted-foreground">
            To see content and participate, request to join.
          </p>
          {group.visibility === "secret" && group.hasPendingInvitation && group.invitationToken && (
            <Button
              onClick={() => acceptInvitation({ token: group.invitationToken! })}
              disabled={isAccepting}
            >
              {isAccepting ? "Accepting..." : "Accept Invitation"}
            </Button>
          )}
          {group.visibility === "private" &&
            !group.hasPendingInvitation &&
            !group.hasPendingJoinRequest && (
              <Button
                onClick={() => requestToJoin({ groupId: groupId as Id<"groups"> })}
                disabled={isRequesting}
              >
                {isRequesting ? "Requesting..." : "Request to Join"}
              </Button>
            )}
          {group.visibility === "private" &&
            group.hasPendingInvitation &&
            group.invitationToken && (
              <Button
                onClick={() => acceptInvitation({ token: group.invitationToken! })}
                disabled={isAccepting}
              >
                {isAccepting ? "Accepting..." : "Accept Invitation"}
              </Button>
            )}
          {group.visibility === "private" && group.hasPendingJoinRequest && (
            <Badge
              variant="outline"
              className="bg-blue-50 px-4 py-1.5 text-sm text-blue-700 border-blue-200"
            >
              Your request is pending approval
            </Badge>
          )}
        </div>
      ) : (
        <JoinGateProvider
          groupId={groupId as Id<"groups">}
          isMember={membership?.isMember ?? true}
          visibility={(group.visibility as "public" | "private" | "secret") ?? "public"}
        >
          {!membership?.isMember && !group.isGlobal && (
            <div className="mb-6 flex items-center gap-2.5 rounded-lg border border-border bg-muted/50 px-4 py-3">
              <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Join this {groupLabel.singularLower} to participate and receive notifications.
              </p>
            </div>
          )}

          <TabNav tabs={tabs} />

          <Outlet />
        </JoinGateProvider>
      )}

      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave {groupLabel.singular}</DialogTitle>
            <DialogDescription>
              Are you sure you want to leave "{group.name}"? You will no longer have access to this
              {` ${groupLabel.singularLower}'s`} updates and discussions.
              {group.visibility === "private" &&
                ` Since this is a private ${groupLabel.singularLower}, you'll need to request to join again.`}
              {group.visibility === "secret" &&
                ` Since this is a secret ${groupLabel.singularLower}, you'll need a new invitation to rejoin.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLeaveDialogOpen(false)}
              disabled={isLeaving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => leaveGroup({ groupId: groupId as Id<"groups"> })}
              disabled={isLeaving}
            >
              {isLeaving ? "Leaving..." : `Leave ${groupLabel.singular}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppPageContent>
  );
}
